import { StandardEvents } from '@shopify/events';

/**
 * Keeps a variant-specific Plan description synchronized with Horizon's
 * server-rendered variant updates.
 */
class VariantPlanDescription extends HTMLElement {
  #abortController;
  #desktopMediaQuery = window.matchMedia('(min-width: 750px)');
  #movableNode;
  #originalParent;
  #originalNextSibling;
  #productInformation;

  connectedCallback() {
    this.#abortController = new AbortController();

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#abortController.signal,
    });

    this.#prepareDesktopPlacement();
    this.#prepareCompatibleVariantOptions();
    this.#desktopMediaQuery.addEventListener('change', this.#syncDesktopPlacement, {
      signal: this.#abortController.signal,
    });
  }

  /**
   * Horizon deliberately leaves impossible option combinations clickable.
   * For plans, that can briefly select an unavailable variant (for example
   * 1 GB / 30 days). Keep later option groups constrained by the option the
   * customer just chose and select the first compatible available variant.
   */
  #prepareCompatibleVariantOptions(attempt = 0) {
    const variantsJson = this.querySelector('[data-product-variants]');
    const productInformation = this.closest('.product-information');
    const picker = productInformation?.querySelector('variant-picker');

    if (!variantsJson) return;
    if (!picker) {
      if (attempt < 10) {
        requestAnimationFrame(() => {
          if (this.isConnected) this.#prepareCompatibleVariantOptions(attempt + 1);
        });
      }
      return;
    }
    if (picker.dataset.compatibleOptionsReady === 'true') return;

    let variants;
    try {
      variants = JSON.parse(variantsJson.textContent || '[]');
    } catch (error) {
      console.warn('[variant-plan-description] Variant data could not be read:', error);
      return;
    }

    if (!variants.length) return;
    const availableVariants = variants.filter((variant) => variant.available);

    picker.dataset.compatibleOptionsReady = 'true';

    const getFieldsets = () => [...picker.querySelectorAll('.variant-option--buttons')];
    const getOptionIndex = (fieldset) => {
      const inputId = fieldset.querySelector('input')?.dataset.inputId;
      return Number.parseInt(inputId?.split('-')[0] || '', 10) - 1;
    };
    const getSelections = (fieldsets) =>
      fieldsets.map((fieldset) => ({
        optionIndex: getOptionIndex(fieldset),
        value: fieldset.querySelector('input:checked')?.value,
      }));

    const setLabelHidden = (input, hidden) => {
      const label = input.closest('.variant-option__button-label');
      if (label && label.hidden !== hidden) label.hidden = hidden;
    };

    const syncOptions = (changedIndex = -1) => {
      const fieldsets = getFieldsets();
      if (fieldsets.length < 2) return;

      const selected = getSelections(fieldsets);

      if (changedIndex >= 0 && changedIndex < fieldsets.length - 1) {
        const candidates = variants.filter((variant) =>
          selected.slice(0, changedIndex + 1).every(
            ({ optionIndex, value }) =>
              value === undefined || variant.options[optionIndex] === value
          )
        );
        const selectableCandidates = candidates.filter((variant) => variant.available);
        const exactMatch = selectableCandidates.find((variant) =>
          selected.every(
            ({ optionIndex, value }) =>
              value === undefined || variant.options[optionIndex] === value
          )
        );
        const compatibleVariant = exactMatch || selectableCandidates[0];

        if (compatibleVariant) {
          fieldsets.slice(changedIndex + 1).forEach((fieldset) => {
            const optionIndex = getOptionIndex(fieldset);
            const input = [...fieldset.querySelectorAll('input')].find(
              (candidate) => candidate.value === compatibleVariant.options[optionIndex]
            );
            if (input) input.checked = true;
          });
        }
      }

      const current = getSelections(fieldsets);
      fieldsets.forEach((fieldset, fieldsetIndex) => {
        if (fieldsetIndex === 0) return;
        const optionIndex = getOptionIndex(fieldset);
        const selectedPrefix = current.slice(0, fieldsetIndex);

        fieldset.querySelectorAll('input').forEach((input) => {
          const compatible = availableVariants.some(
            (variant) =>
              variant.options[optionIndex] === input.value &&
              selectedPrefix.every(
                ({ optionIndex: previousOptionIndex, value }) =>
                  value === undefined || variant.options[previousOptionIndex] === value
              )
          );
          setLabelHidden(input, !compatible);
        });
      });
    };

    picker.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

        const fieldsets = getFieldsets();
        const changedIndex = fieldsets.indexOf(input.closest('fieldset'));
        syncOptions(changedIndex);
      },
      { capture: true, signal: this.#abortController.signal }
    );

    const observer = new MutationObserver(() => syncOptions());
    observer.observe(picker, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-option-available', 'aria-disabled'],
    });
    this.#abortController.signal.addEventListener('abort', () => observer.disconnect());

    syncOptions();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  /**
   * The template contains two copies of this block: one inside product details
   * for mobile, and one at product-information level for desktop. Move only the
   * desktop copy into the media column so it sits directly below the image and
   * is not pushed down by the height of the common Product description.
   */
  #prepareDesktopPlacement() {
    if (this.closest('.product-details')) return;

    if (!this.#productInformation) {
      this.#productInformation = this.closest('.product-information');
    }

    if (!this.#productInformation || this.#movableNode) {
      this.#syncDesktopPlacement();
      return;
    }

    const parent = this.parentElement;
    this.#movableNode =
      parent?.parentElement === this.#productInformation ? parent : this;
    this.#originalParent = this.#movableNode.parentNode;
    this.#originalNextSibling = this.#movableNode.nextSibling;

    this.#syncDesktopPlacement();
  }

  #syncDesktopPlacement = () => {
    if (!this.#movableNode || !this.#originalParent || !this.#productInformation) return;

    if (this.#desktopMediaQuery.matches) {
      const media = this.#productInformation.querySelector('.product-information__media');
      if (media && this.#movableNode.parentNode !== media) {
        media.append(this.#movableNode);
      }
      return;
    }

    if (this.#movableNode.parentNode !== this.#originalParent) {
      const referenceNode =
        this.#originalNextSibling?.parentNode === this.#originalParent
          ? this.#originalNextSibling
          : null;
      this.#originalParent.insertBefore(this.#movableNode, referenceNode);
    }
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const updatedBlock = detail.html.querySelector(
          `variant-plan-description[data-block-id="${CSS.escape(this.dataset.blockId ?? '')}"]`
        );

        if (!updatedBlock) return;
        const currentVariants = this.querySelector('[data-product-variants]');
        this.innerHTML = updatedBlock.innerHTML;
        if (currentVariants && !this.querySelector('[data-product-variants]')) {
          this.prepend(currentVariants);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[variant-plan-description] Variant update failed:', error);
        }
      });
  };
}

if (!customElements.get('variant-plan-description')) {
  customElements.define('variant-plan-description', VariantPlanDescription);
}
